import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

const STORAGE_KEY = 'h2s_ui_v2';

/**
 * Switch for the v2 redesign (softer look, new navigation shell, Today screen).
 *
 * Resolution order:
 *  1. A saved choice (Settings → Debug in dev builds, or `?v2=1` / `?v2=0` in the URL).
 *  2. The build default: on for dev builds, off for production builds.
 *
 * Production stays on the current app until v2 is ready; flip `v2Default` in
 * `environment.prod.ts` (or move this to a per-org feature flag) to release it.
 */
@Injectable({ providedIn: 'root' })
export class UiV2Service {
  isEnabled(): boolean {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === '1') return true;
    if (saved === '0') return false;
    return !!(environment as { v2Default?: boolean }).v2Default;
  }

  /**
   * Whether the old app should offer a "Try the new design" link: in dev builds, and in any build where v2 is the
   * default. Production stays hidden until `v2Default` is flipped, so nobody finds v2 early.
   */
  offered(): boolean {
    return !environment.production || !!(environment as { v2Default?: boolean }).v2Default;
  }

  /** Save the choice and reload, since the app shell is chosen once at route-match time. */
  set(enabled: boolean): void {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
    window.location.reload();
  }

  /** Call once at startup: honours ?v2=1 / ?v2=0 and tags <body> so v2 styles apply. */
  init(): void {
    const q = new URLSearchParams(window.location.search).get('v2');
    if (q === '1' || q === '0') localStorage.setItem(STORAGE_KEY, q);
    document.body.classList.toggle('v2', this.isEnabled());
  }
}
